/**
 * Página de auditoría de accesos (Ley N° 29733).
 *
 * SOLO LECTURA. El log es append-only por diseño y por ley.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import {
  type AccionAudit,
  type AuditoriaEntry,
  listAuditoria,
} from "../api/auditoria";
import { Alert, Badge, Card, CardBody, CardHeader, Field } from "../components/ui";
import { useZonaHoraria } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";

const ACCIONES: { value: AccionAudit; label: string }[] = [
  { value: "LEER", label: "Lectura" },
  { value: "CREAR", label: "Creación" },
  { value: "MODIFICAR", label: "Modificación" },
  { value: "ELIMINAR", label: "Eliminación" },
];

const ENTIDADES = [
  { value: "paciente", label: "Pacientes" },
  // Cuando lleguen episodios/recetas, los añadimos
];

const BADGE_VARIANT_BY_ACCION: Record<string, "success" | "neutral" | "warning" | "danger"> = {
  LEER: "neutral",
  CREAR: "success",
  MODIFICAR: "warning",
  ELIMINAR: "danger",
};

export function AuditoriaPage() {
  const [accion, setAccion] = useState<AccionAudit | "">("");
  const [entidad, setEntidad] = useState<string>("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["auditoria", accion, entidad],
    queryFn: () =>
      listAuditoria({
        accion: accion || undefined,
        entidad: entidad || undefined,
      }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Auditoría de accesos</h1>
        <p className="text-sm text-slate-500 mt-1">
          Registro inmutable de todos los accesos a datos sensibles. Cumple Ley N° 29733
          de Protección de Datos Personales.
        </p>
      </div>

      <Alert variant="info">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">
              Este log no se puede modificar ni borrar.
            </p>
            <p className="text-xs mt-1">
              Cada operación que toca data del paciente queda registrada con usuario, IP, fecha y operación.
              Útil para investigar accesos indebidos o presentar evidencia ante la ANPDP.
            </p>
          </div>
        </div>
      </Alert>

      <Card>
        <CardHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Acción" htmlFor="f_accion">
              <select
                id="f_accion"
                value={accion}
                onChange={(e) => setAccion(e.target.value as AccionAudit | "")}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Todas</option>
                {ACCIONES.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Entidad" htmlFor="f_entidad">
              <select
                id="f_entidad"
                value={entidad}
                onChange={(e) => setEntidad(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Todas</option>
                {ENTIDADES.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}
          {error && (
            <div className="px-6 py-4">
              <Alert>No se pudo cargar el log de auditoría.</Alert>
            </div>
          )}
          {data && data.length === 0 && !isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No hay registros con esos filtros. Si acabas de empezar a usar el sistema,
              registra algunos pacientes y vuelve aquí.
            </div>
          )}
          {data && data.length > 0 && <AuditoriaTable rows={data} />}
        </CardBody>
      </Card>
    </div>
  );
}

function AuditoriaTable({ rows }: { rows: AuditoriaEntry[] }) {
  const tz = useZonaHoraria();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-200">
          <tr>
            <Th>Fecha</Th>
            <Th>Usuario</Th>
            <Th>Acción</Th>
            <Th>Entidad</Th>
            <Th>Path</Th>
            <Th>Status</Th>
            <Th>IP</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
              <td className="px-6 py-2.5 text-xs whitespace-nowrap text-slate-700">
                {fechaHora(r.ocurrido_en, tz)}
              </td>
              <td className="px-6 py-2.5">
                <div className="text-slate-900 text-xs">
                  {r.usuario_email ?? "—"}
                </div>
                {r.usuario_id && (
                  <div className="text-[10px] text-slate-400 font-mono">
                    id={r.usuario_id}
                  </div>
                )}
              </td>
              <td className="px-6 py-2.5">
                <Badge variant={BADGE_VARIANT_BY_ACCION[r.accion] ?? "neutral"}>
                  {r.accion}
                </Badge>
              </td>
              <td className="px-6 py-2.5 text-slate-700 text-xs">
                {r.entidad}
                {r.entidad_id !== null && (
                  <span className="ml-1 text-slate-400 font-mono">#{r.entidad_id}</span>
                )}
              </td>
              <td className="px-6 py-2.5 font-mono text-[11px] text-slate-600 max-w-[280px] truncate">
                {r.metodo_http} {r.path}
              </td>
              <td className="px-6 py-2.5">
                {r.status_code != null && (
                  <Badge
                    variant={
                      r.status_code < 300 ? "success" : r.status_code < 400 ? "warning" : "danger"
                    }
                  >
                    {r.status_code}
                  </Badge>
                )}
              </td>
              <td className="px-6 py-2.5 text-[11px] font-mono text-slate-500">
                {r.ip ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}
